import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  List, 
  Tag, 
  Typography, 
  Empty, 
  Spin, 
  Button, 
  Space, 
  Divider,
  Card,
  Row,
  Col,
  Alert,
  message,
  Tabs,
  Form,
  Select,
  Input,
  Radio
} from 'antd';
import { 
  GoogleOutlined, 
  FacebookOutlined, 
  BarChartOutlined,
  DatabaseOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { auditAPI } from '../../../services/api';
import dayjs from 'dayjs';

// Meta Logo Component (using the existing SVG design)
const MetaIcon = ({ style }) => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 32 32" 
    fill="currentColor"
    style={{ 
      verticalAlign: 'middle', 
      display: 'inline-block',
      ...style 
    }}
  >
    <path d="M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6  C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1  c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5  c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7  C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11"/>
  </svg>
);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Date range options for audit form
const GOOGLE_ADS_DATE_RANGES = [
  { value: 'TODAY', label: 'Bugün' },
  { value: 'YESTERDAY', label: 'Dün' },
  { value: 'LAST_7_DAYS', label: 'Son 7 Gün' },
  { value: 'LAST_14_DAYS', label: 'Son 14 Gün' },
  { value: 'LAST_30_DAYS', label: 'Son 30 Gün' },
  { value: 'THIS_MONTH', label: 'Bu Ay' },
  { value: 'LAST_MONTH', label: 'Geçen Ay' }
];

const FACEBOOK_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_7d', label: 'Son 7 Gün' },
  { value: 'last_14d', label: 'Son 14 Gün' },
  { value: 'last_28d', label: 'Son 28 Gün' },
  { value: 'last_30d', label: 'Son 30 Gün' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const GOOGLE_ANALYTICS_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_7_days', label: 'Son 7 Gün' },
  { value: 'last_14_days', label: 'Son 14 Gün' },
  { value: 'last_30_days', label: 'Son 30 Gün' },
  { value: 'last_90_days', label: 'Son 90 Gün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const CUSTOM_DATA_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const BrandAuditHistoryModal = ({ 
  visible, 
  onCancel, 
  brand 
}) => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('new-audit');
  const [auditForm] = Form.useForm();
  const [selectedAuditType, setSelectedAuditType] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [auditButtonLoading, setAuditButtonLoading] = useState(false);
  const [auditAnimation, setAuditAnimation] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Load audits when modal opens
  useEffect(() => {
    if (visible && brand) {
      loadBrandAudits();
      loadBrandCapabilities();
      
      // Form'u reset et ve default değerleri ayarla
      auditForm.resetFields();
      setSelectedAuditType(null);
    }
  }, [visible, brand]);

  const loadBrandCapabilities = async () => {
    try {
      const response = await auditAPI.getBrandAuditCapabilities(brand.id);
      setCapabilities(response.data);
      
      // Auto-select audit type if only one is available
      const availableTypes = [];
      if (response.data.hasGoogleAds) availableTypes.push('google_ads');
      if (response.data.hasFacebookAds) availableTypes.push('facebook');
      if (response.data.hasGoogleAnalytics) availableTypes.push('google_analytics');
      if (response.data.hasCustomData) availableTypes.push('custom_data');
      
      if (availableTypes.length === 1) {
        setSelectedAuditType(availableTypes[0]);
        auditForm.setFieldsValue({ auditType: availableTypes[0] });
      }
    } catch (error) {
      console.error('Brand capabilities loading error:', error);
      setCapabilities(null);
    }
  };

  const loadBrandAudits = async () => {
    try {
      setLoading(true);
      
      // Tüm markalar için API'dan veri çek (Brand ID 1 de dahil)
      const response = await auditAPI.getBrandAudits(brand.id, {
        limit: 20, // Son 20 audit
        page: 1,
        status: 'success' // Sadece başarılı audit'leri getir
      });
      const audits = response.data.audits || [];
      console.log('Loaded audits:', audits);
      if (audits.length > 0) {
        console.log('First audit structure:', audits[0]);
      }
      setAudits(audits);
    } catch (error) {
      console.error('Brand audits loading error:', error);
      setAudits([]);
    } finally {
      setLoading(false);
    }
  };

  const getAuditIcon = (audit) => {
    // Hem auditType hem de audit_type field'larını kontrol et
    const auditType = audit.auditType || audit.audit_type;
    if (auditType === 'google_ads') {
      return <GoogleOutlined style={{ fontSize: 20, color: '#4285F4' }} />;
    }
    if (auditType === 'facebook') {
      return <FacebookOutlined style={{ fontSize: 20, color: '#1877F2' }} />;
    }
    if (auditType === 'google_analytics') {
      return <BarChartOutlined style={{ fontSize: 20, color: '#EA4335' }} />;
    }
    if (auditType === 'custom_data') {
      return <DatabaseOutlined style={{ fontSize: 20, color: '#722ED1' }} />;
    }
    return <FacebookOutlined style={{ fontSize: 20, color: '#1877F2' }} />;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#f5222d' }} />;
      case 'pending':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success': return 'Başarılı';
      case 'error': return 'Hatalı';
      case 'pending': return 'Beklemede';
      default: return 'Bilinmiyor';
    }
  };

  const handleViewDetails = (audit) => {
    setSelectedAudit(audit);
    setDetailModalVisible(true);
  };

  const handleDetailClose = () => {
    setDetailModalVisible(false);
    setSelectedAudit(null);
  };

  const handleAuditTypeChange = (value) => {
    setSelectedAuditType(value);
    // Sadece audit type'ı set et, diğer alanları sıfırlama
    auditForm.setFieldsValue({
      auditType: value
    });
  };

  const getDateRangeOptions = () => {
    if (selectedAuditType === 'google_ads') {
      return GOOGLE_ADS_DATE_RANGES;
    }
    if (selectedAuditType === 'facebook') {
      return FACEBOOK_DATE_RANGES;
    }
    if (selectedAuditType === 'google_analytics') {
      return GOOGLE_ANALYTICS_DATE_RANGES;
    }
    if (selectedAuditType === 'custom_data') {
      return CUSTOM_DATA_DATE_RANGES;
    }
    return [];
  };

  // Audit animasyon adımları
  const getStepText = () => {
    const platformName = getPlatformName();
    const steps = [
      `🔗 ${platformName}'a Bağlanılıyor...`,
      `📊 Verilere Erişiliyor...`,
      `📈 Metrikler Analiz Ediliyor...`,
      `🤖 AI ile Performans Değerlendiriliyor...`,
      `📝 Rapor Hazırlanıyor...`
    ];
    return steps[currentStep] || '';
  };

  const getPlatformName = () => {
    if (selectedAuditType === 'google_ads') return 'Google Ads';
    if (selectedAuditType === 'facebook') return 'Meta Ads';
    if (selectedAuditType === 'google_analytics') return 'Google Analytics 4';
    if (selectedAuditType === 'custom_data') return 'ReportsUp';
    return 'Platform';
  };

  // Random audit süresi hesapla (9-13 saniye)
  const getRandomAuditDuration = () => {
    return Math.floor(Math.random() * (13000 - 9000 + 1)) + 9000; // 9000-13000ms arası
  };

  // Toplam süreyi 5 adıma random dağıt
  const distributeDuration = (totalMs) => {
    const steps = 5;
    const baseTime = Math.floor(totalMs / steps); // Her adım için minimum süre
    const remainingTime = totalMs - (baseTime * steps);
    
    const durations = [];
    let remaining = remainingTime;
    
    for (let i = 0; i < steps; i++) {
      const extraTime = i < steps - 1 ? Math.floor(Math.random() * (remaining / (steps - i))) : remaining;
      durations.push(baseTime + extraTime);
      remaining -= extraTime;
    }
    
    return durations;
  };

  // Progress bar'ı smooth şekilde güncelle
  const updateProgressSmoothly = (startPercent, endPercent, duration) => {
    const startTime = Date.now();
    const progressDiff = endPercent - startPercent;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out animasyon
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentPercent = startPercent + (progressDiff * easeOut);
      
      setProgressPercentage(currentPercent);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const handleAuditStart = async () => {
    try {
      // Form validation
      await auditForm.validateFields();
    } catch (validationError) {
      console.log('Form validation failed:', validationError);
      return;
    }

    setAuditButtonLoading(true);
    setAuditAnimation(true);
    setCurrentStep(0);
    setProgressPercentage(0);

    // Demo marka (ID: 1) için farklı işlem
    if (brand.id === 1) {
      // Random audit süresi ve adım sürelerini hesapla
      const totalDuration = getRandomAuditDuration();
      const stepDurations = distributeDuration(totalDuration);
      
      console.log(`🤖 Audit süresi: ${(totalDuration/1000).toFixed(1)} saniye`);
      console.log(`📊 Adım süreleri:`, stepDurations.map(d => `${(d/1000).toFixed(1)}s`));
      
      // Demo audit oluştur ve animasyon göster
      const animationSteps = [
        () => {
          setCurrentStep(0); // Bağlanılıyor
          updateProgressSmoothly(0, 20, stepDurations[0]);
        },
        () => {
          setCurrentStep(1); // Verilere Erişiliyor
          updateProgressSmoothly(20, 40, stepDurations[1]);
        },
        () => {
          setCurrentStep(2); // Metrikler Analiz Ediliyor
          updateProgressSmoothly(40, 60, stepDurations[2]);
        },
        () => {
          setCurrentStep(3); // AI ile Performans Değerlendiriliyor
          updateProgressSmoothly(60, 80, stepDurations[3]);
        },
        () => {
          setCurrentStep(4); // Rapor Hazırlanıyor
          updateProgressSmoothly(80, 100, stepDurations[4]);
        },
        async () => {
          try {
            // Gerçek API çağrısı yap (demo audit döndürecek)
            const formValues = auditForm.getFieldsValue(true); // Get all field values including undefined ones
            console.log('Form values:', formValues);
            console.log('Selected audit type:', selectedAuditType);
            
            const auditData = {
              auditType: selectedAuditType,
              dateRange: formValues.dateRange,
              prompt: formValues.prompt || `${selectedAuditType} performansını analiz et`,
              model: formValues.model || 'gpt-4'
            };
            
            console.log('Sending audit data:', auditData);

            const response = await auditAPI.triggerBrandAudit(brand.id, auditData);
            
            // Başarılı audit'i listeye ekle
            const newAudit = response.data.audit;
            setAudits(prevAudits => [newAudit, ...prevAudits]);
            
            // Başarı mesajı ve audit sonucunu modal'da göster
            message.success(`✨ Demo audit ${(totalDuration/1000).toFixed(1)} saniyede tamamlandı!`);
            setSelectedAudit(newAudit);
            setDetailModalVisible(true);
            
            // Form'u sıfırla
            auditForm.resetFields();
            setSelectedAuditType(null);
            
          } catch (error) {
            console.error('Demo audit error:', error);
            message.error(error.response?.data?.error || 'Demo audit sırasında hata oluştu');
          } finally {
            // Animasyon tamamlandı
            setAuditAnimation(false);
            setAuditButtonLoading(false);
            setCurrentStep(0);
          }
        }
      ];

      // Her adımı hesaplanan random sürelerde çalıştır
      let cumulativeTime = 0;
      animationSteps.forEach((step, index) => {
        if (index < stepDurations.length) {
          cumulativeTime += stepDurations[index];
        }
        setTimeout(step, cumulativeTime);
      });
    } else {
      // Gerçek markalar için mevcut işlem
      const animationSteps = [
        () => setCurrentStep(0), // Bağlanılıyor
        () => setCurrentStep(1), // Verilere Erişiliyor
        () => setCurrentStep(2), // Metrikler Analiz Ediliyor
        () => setCurrentStep(3), // AI ile Performans Değerlendiriliyor
        () => setCurrentStep(4), // Rapor Hazırlanıyor
        () => {
          // Animasyon tamamlandı
          setAuditAnimation(false);
          setAuditButtonLoading(false);
          setCurrentStep(0);
          message.info('✨ Audit Ai şu anda BusinessUp! uzmanları tarafından kullanılmaktadır. Yakında sizlerin kullanımına açılacaktır.', 6);
        }
      ];

      // Her adımı 2 saniye arayla çalıştır
      animationSteps.forEach((step, index) => {
        setTimeout(step, index * 2000);
      });
    }
  };

  const getAccountInfo = () => {
    if (!selectedAuditType || !capabilities) return null;

    if (selectedAuditType === 'google_ads' && capabilities.googleAdsCodes?.length > 0) {
      const code = capabilities.googleAdsCodes[0];
      return {
        type: 'Google Ads',
        icon: <GoogleOutlined style={{ color: '#4285F4' }} />,
        accountId: code.parameters?.customerId,
        name: code.name
      };
    }

    if (selectedAuditType === 'facebook' && capabilities.facebookAdsCodes?.length > 0) {
      const code = capabilities.facebookAdsCodes[0];
      return {
        type: 'Meta Ads',
        icon: <MetaIcon style={{ color: '#1877F2' }} />,
        accountId: code.parameters?.adAccountId,
        name: code.name
      };
    }

    if (selectedAuditType === 'google_analytics' && capabilities.googleAnalyticsCodes?.length > 0) {
      const code = capabilities.googleAnalyticsCodes[0];
      return {
        type: 'Google Analytics',
        icon: <BarChartOutlined style={{ color: '#EA4335' }} />,
        accountId: code.parameters?.id,
        name: code.name
      };
    }

    if (selectedAuditType === 'custom_data') {
      return {
        type: 'ReportsUp Analizi',
        icon: <DatabaseOutlined style={{ color: '#722ED1' }} />,
        accountId: `brand_${brand?.id}`,
        name: `${brand?.name} Marka Verileri`
      };
    }

    return null;
  };

  // Gelişmiş markdown renderer
  const renderMarkdown = (text) => {
    if (!text) return '';
    
    let html = text;
    
    // Tables (| Header | Header |)
    html = html.replace(
      /(\|[^\n]+\|\n)(\|[-:\s|]+\|\n)((\|[^\n]+\|\n?)+)/gm,
      (match, header, separator, rows) => {
        const headerCells = header.split('|').slice(1, -1).map(cell => 
          `<th style="padding: 8px 12px; border: 1px solid #d9d9d9; background: #fafafa; font-weight: bold;">${cell.trim()}</th>`
        ).join('');
        
        const bodyRows = rows.trim().split('\n').map(row => {
          const cells = row.split('|').slice(1, -1).map(cell => 
            `<td style="padding: 8px 12px; border: 1px solid #d9d9d9;">${cell.trim()}</td>`
          ).join('');
          return `<tr>${cells}</tr>`;
        }).join('');
        
        return `<table style="border-collapse: collapse; width: 100%; margin: 16px 0; font-size: 14px;">
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>`;
      }
    );
    
    // Code blocks (```code```)
    html = html.replace(
      /```([\s\S]*?)```/g, 
      '<pre style="background: #f6f8fa; padding: 16px; border-radius: 6px; border: 1px solid #e1e4e8; margin: 16px 0; overflow-x: auto;"><code style="font-family: SFMono-Regular, Consolas, Monaco, monospace; font-size: 13px; line-height: 1.45;">$1</code></pre>'
    );
    
    // Inline code (`code`)
    html = html.replace(
      /`([^`]+)`/g, 
      '<code style="background: #f6f8fa; padding: 2px 4px; border-radius: 3px; font-family: SFMono-Regular, Consolas, Monaco, monospace; font-size: 85%; border: 1px solid #e1e4e8;">$1</code>'
    );
    
    // Bold text (**text**)
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic text (*text*)
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Headers
    html = html.replace(/^#### (.*$)/gm, '<h5 style="margin: 12px 0 6px 0; font-size: 13px; font-weight: bold; color: #1890ff;">$1</h5>');
    html = html.replace(/^### (.*$)/gm, '<h4 style="margin: 12px 0 6px 0; font-size: 14px; font-weight: bold; color: #1890ff;">$1</h4>');
    html = html.replace(/^## (.*$)/gm, '<h3 style="margin: 16px 0 8px 0; font-size: 16px; font-weight: bold; color: #1890ff;">$1</h3>');
    html = html.replace(/^# (.*$)/gm, '<h2 style="margin: 20px 0 10px 0; font-size: 18px; font-weight: bold; color: #1890ff;">$1</h2>');
    
    // Links [text](url)
    html = html.replace(
      /\[([^\]]+)\]\(([^\)]+)\)/g, 
      '<a href="$2" style="color: #1890ff; text-decoration: none;" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    
    // Blockquotes (> text)
    html = html.replace(
      /^> (.*$)/gm, 
      '<blockquote style="border-left: 4px solid #1890ff; margin: 16px 0; padding: 8px 16px; background: #f6f8fa; font-style: italic;">$1</blockquote>'
    );
    
    // Horizontal rules (---)
    html = html.replace(/^---$/gm, '<hr style="border: none; border-top: 1px solid #e1e4e8; margin: 14px 0;" />');
    
    // Unordered lists (- text or * text)
    html = html.replace(/^[\-\*] (.*$)/gm, '<li style="margin: 4px 0; list-style-type: disc; margin-left: 20px;">$1</li>');
    
    // Ordered lists (1. text)
    html = html.replace(/^(\d+)\. (.*$)/gm, '<li style="margin: 4px 0; list-style-type: decimal; margin-left: 20px;">$2</li>');
    
    // Wrap consecutive list items in ul/ol tags
    html = html.replace(/((<li[^>]*>.*<\/li>\s*){2,})/g, '<ul style="margin: 8px 0; padding-left: 0;">$1</ul>');
    
    // Line breaks
    html = html.replace(/\n/g, '<br/>');
    
    return html;
  };


  // Date range değerini Türkçe'ye çevir
  const getDateRangeLabel = (dateRange) => {
    if (!dateRange) return 'Son 7 Gün';
    
    const labelMap = {
      'TODAY': 'Bugün',
      'today': 'Bugün',
      'YESTERDAY': 'Dün',
      'yesterday': 'Dün',
      'LAST_7_DAYS': 'Son 7 Gün',
      'last_7d': 'Son 7 Gün',
      'LAST_14_DAYS': 'Son 14 Gün',
      'last_14d': 'Son 14 Gün',
      'LAST_30_DAYS': 'Son 30 Gün',
      'last_30d': 'Son 30 Gün',
      'last_28d': 'Son 28 Gün',
      'THIS_MONTH': 'Bu Ay',
      'this_month': 'Bu Ay',
      'LAST_MONTH': 'Geçen Ay',
      'last_month': 'Geçen Ay',
      'last_90_days': 'Son 90 Gün',
      'this_week': 'Bu Hafta',
      'last_week': 'Geçen Hafta'
    };
    
    return labelMap[dateRange] || dateRange;
  };

  // PDF indirme fonksiyonu - Browser Print API
  const downloadAuditPDF = () => {
    if (!selectedAudit || !selectedAudit.analysis) return;
    
    try {
      // Markdown'dan HTML'e çevir
      const htmlContent = renderMarkdown(selectedAudit.analysis);
      
      // Print penceresi için HTML oluştur
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Audit AI Raporu - ${brand?.name || 'Marka'}</title>
          <style>
            @page {
              margin: 2cm;
              size: A4;
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            }
            
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              margin: 0;
              padding: 0;
              font-size: 14px;
              background: white;
            }
            
            .header {
              text-align: center;
              margin-bottom: 30px;
              padding: 20px 0;
              border-bottom: 3px solid #1890ff;
            }
            
            .header h1 {
              color: #1890ff;
              margin: 0 0 10px 0;
              font-size: 24px;
              font-weight: bold;
            }
            
            .header h2 {
              color: #666;
              margin: 5px 0;
              font-size: 18px;
              font-weight: normal;
            }
            
            .header h3 {
              color: #888;
              margin: 5px 0;
              font-size: 16px;
              font-weight: normal;
            }
            
            .info-box {
              background: #f8f9fa;
              border: 1px solid #e9ecef;
              border-left: 4px solid #1890ff;
              padding: 15px;
              margin: 20px 0;
              border-radius: 4px;
            }
            
            .content {
              margin: 25px 0;
              line-height: 1.8;
            }
            
            .content h2 {
              color: #1890ff;
              margin-top: 25px;
              margin-bottom: 10px;
              font-size: 18px;
              font-weight: bold;
              page-break-inside: avoid;
            }
            
            .content h3 {
              color: #1890ff;
              margin-top: 20px;
              margin-bottom: 8px;
              font-size: 16px;
              font-weight: bold;
              page-break-inside: avoid;
            }
            
            .content h4, .content h5 {
              color: #1890ff;
              margin-top: 15px;
              margin-bottom: 6px;
              font-weight: bold;
              page-break-inside: avoid;
            }
            
            .content p {
              margin: 10px 0;
              text-align: justify;
            }
            
            .content ul, .content ol {
              margin: 10px 0;
              padding-left: 25px;
            }
            
            .content li {
              margin: 5px 0;
            }
            
            .content table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 12px;
              page-break-inside: avoid;
            }
            
            .content th, .content td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            
            .content th {
              background-color: #f8f9fa;
              font-weight: bold;
            }
            
            .content code {
              background: #f1f3f4;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
              font-size: 12px;
            }
            
            .content pre {
              background: #f8f9fa;
              border: 1px solid #e9ecef;
              border-radius: 4px;
              padding: 15px;
              margin: 15px 0;
              overflow-x: auto;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              page-break-inside: avoid;
            }
            
            .content blockquote {
              border-left: 4px solid #1890ff;
              margin: 15px 0;
              padding: 10px 20px;
              background: #f8f9fa;
              font-style: italic;
              page-break-inside: avoid;
            }
            
            .content strong {
              font-weight: bold;
              color: #000;
            }
            
            .content em {
              font-style: italic;
            }
            
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e9ecef;
              text-align: center;
              font-size: 12px;
              color: #6c757d;
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🤖 AUDIT AI RAPORU</h1>
            <h2>${brand?.name || 'Marka'}</h2>
            <h3>${getAuditTitle(selectedAudit)}</h3>
          </div>
          
          <div class="info-box">
            <strong>📅 Analiz Tarihi:</strong> ${dayjs(selectedAudit.created_at).format('DD MMMM YYYY, HH:mm')}<br>
            <strong>📊 Analiz Dönemi:</strong> ${getDateRangeLabel(selectedAudit.dateRange)}<br>
            <strong>🤖 AI Model:</strong> Audit AI
          </div>
          
          <div class="content">
            ${htmlContent}
          </div>
          
          <div class="footer">
            <div style="margin-bottom: 10px;">
              <strong>Bu rapor BusinessUp Audit AI tarafından otomatik olarak oluşturulmuştur.</strong>
            </div>
            <div>
              Rapor Oluşturma Tarihi: ${dayjs().format('DD MMMM YYYY, HH:mm')} | 
              BusinessUp © ${dayjs().format('YYYY')}
            </div>
          </div>
          
          <script>
            window.onload = function() {
              // Kısa bekleme sonrası print dialog açılsın
              setTimeout(function() {
                window.print();
                
                // Print dialog kapandıktan sonra pencereyi kapat
                window.onafterprint = function() {
                  window.close();
                };
              }, 500);
            };
          </script>
        </body>
        </html>
      `;
      
      // Yeni pencere aç ve print et
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        message.success('PDF yazdırma penceresi açılıyor...');
      } else {
        message.error('Popup engelleyici aktif. Lütfen devre dışı bırakın.');
      }
      
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      message.error('PDF oluşturulurken hata oluştu.');
    }
  };

  // Date range'i gerçek tarihe çevir
  const getAuditTitle = (audit) => {
    const auditType = audit.auditType || audit.audit_type;
    if (auditType === 'google_ads') {
      return 'GOOGLE ADS KAMPANYA ANALİZ RAPORU';
    }
    if (auditType === 'facebook') {
      return 'FACEBOOK KAMPANYA ANALİZ RAPORU';
    }
    if (auditType === 'google_analytics') {
      return 'GOOGLE ANALYTICS KAMPANYA ANALİZ RAPORU';
    }
    if (auditType === 'custom_data') {
      return 'REPORTSUP KAMPANYA ANALİZ RAPORU';
    }
    return 'Bilinmiyor';
  };

  const getDateRangeText = (audit) => {
    const auditDate = dayjs(audit.created_at);
    const dateRange = audit.dateRange || audit.date_range;
    
    // Audit tarihinden geriye doğru hesapla
    if (!dateRange) {
      // Default 7 gün geriye
      const endDate = auditDate;
      const startDate = auditDate.subtract(7, 'day');
      return `${startDate.format('DD')}-${endDate.format('DD MMM')}`;
    }
    
    // Gerçek tarih aralıklarını hesapla
    let startDate, endDate;
    
    switch (dateRange) {
      case 'TODAY':
      case 'today':
        startDate = endDate = auditDate;
        break;
        
      case 'YESTERDAY':  
      case 'yesterday':
        startDate = endDate = auditDate.subtract(1, 'day');
        break;
        
      case 'LAST_7_DAYS':
      case 'last_7d':
        endDate = auditDate;
        startDate = auditDate.subtract(7, 'day');
        break;
        
      case 'LAST_14_DAYS':
      case 'last_14d':
        endDate = auditDate;
        startDate = auditDate.subtract(14, 'day');
        break;
        
      case 'LAST_30_DAYS':
      case 'last_30d':
        endDate = auditDate;
        startDate = auditDate.subtract(30, 'day');
        break;
        
      case 'LAST_MONTH':
      case 'last_month':
        startDate = auditDate.subtract(1, 'month').startOf('month');
        endDate = auditDate.subtract(1, 'month').endOf('month');
        break;
        
      case 'THIS_MONTH':
      case 'this_month':
        startDate = auditDate.startOf('month');
        endDate = auditDate.endOf('month');
        break;
        
      default:
        // Default: Son 7 gün
        endDate = auditDate;
        startDate = auditDate.subtract(7, 'day');
        break;
    }
    
    // Tarihleri formatla
    if (startDate.isSame(endDate, 'day')) {
      return startDate.format('DD MMM');
    } else if (startDate.isSame(endDate, 'month')) {
      return `${startDate.format('DD')}-${endDate.format('DD MMM')}`;
    } else {
      return `${startDate.format('DD MMM')}-${endDate.format('DD MMM')}`;
    }
  };

  return (
    <>
      <Modal
        title={
          <div>
            <Title level={4} style={{ margin: 0, fontSize: window.innerWidth < 640 ? '16px' : '20px' }}>
              <span className="block sm:hidden">{brand?.name?.substring(0, 20)}... ✨ Audit Ai (Beta)</span>
              <span className="hidden sm:inline">{brand?.name} - ✨ Audit Ai (Beta)</span>
            </Title>
            <Alert
              message="✨ Audit Ai Hakkında"
              description="✨ Audit Ai, en son Ai teknolojilerini kullanarak performans analizi yapan gelişmiş bir araç olarak tasarlandı. Şu anki prototip versiyonunda bazı veri hataları veya yanlış yorumlamalar içerebilir. Audit sonuçlarını incelerken bu gibi olasılıkları göz önünde bulundurmanızı öneririz. Yeni versiyonlarla birlikte gelişmeler sağlandıkça sizleri bilgilendireceğiz. Keyifli analizler dileriz!"
              type="info"
              showIcon
              style={{ 
                marginTop: 12, 
                fontSize: '10px',
                marginLeft: 8,
                marginRight: 8
              }}
              className="text-xs"
            />
          </div>
        }
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={window.innerWidth < 640 ? '95vw' : 800}
        style={{ top: window.innerWidth < 640 ? 20 : 50 }}
        bodyStyle={{ 
          maxHeight: window.innerWidth < 640 ? '70vh' : '65vh', 
          overflow: 'auto',
          padding: window.innerWidth < 640 ? '8px' : '12px'
        }}
        zIndex={1000}
      >
        {/* CSS Animasyon */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        
        <Spin spinning={loading}>
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
              {
                key: 'new-audit',
                label: '✨ Audit Ai',
                children: (
                  <div style={{ padding: '8px 0' }}>
                    <Form
                      form={auditForm}
                      layout="vertical"
                    >
                      <Form.Item
                        label="Audit Türü"
                        name="auditType"
                      >
                        <Radio.Group onChange={(e) => handleAuditTypeChange(e.target.value)}>
                          {capabilities?.hasGoogleAds && (
                            <Radio.Button value="google_ads">
                              <Space>
                                <GoogleOutlined style={{ color: '#4285F4' }} />
                                Google Ads
                              </Space>
                            </Radio.Button>
                          )}
                          {capabilities?.hasFacebookAds && (
                            <Radio.Button value="facebook">
                              <Space>
                                <MetaIcon style={{ color: '#1877F2' }} />
                                Meta Ads
                              </Space>
                            </Radio.Button>
                          )}
                          {capabilities?.hasGoogleAnalytics && (
                            <Radio.Button value="google_analytics">
                              <Space>
                                <BarChartOutlined style={{ color: '#EA4335' }} />
                                Google Analytics
                              </Space>
                            </Radio.Button>
                          )}
                          {capabilities?.hasCustomData && (
                            <Radio.Button value="custom_data">
                              <Space>
                                <DatabaseOutlined style={{ color: '#722ED1' }} />
                                ReportsUp
                              </Space>
                            </Radio.Button>
                          )}
                        </Radio.Group>
                      </Form.Item>


                      {selectedAuditType && (
                        <>
                          {/* Audit Ayarları - Animasyon yokken göster */}
                          {!auditAnimation && (
                            <>
                              <Divider />
                              
                              <Title level={5}>Audit Ayarları</Title>
                              
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    label="Tarih Aralığı"
                                    name="dateRange"
                                    rules={[{ required: true, message: 'Tarih aralığı seçin' }]}
                                  >
                                    <Select placeholder="Tarih aralığı" allowClear>
                                      {getDateRangeOptions().map(range => (
                                        <Option key={range.value} value={range.value}>
                                          {range.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="Ai Model"
                                    name="model"
                                    initialValue="audit-ai"
                                  >
                                    <Select placeholder="Model seçin" allowClear>
                                      <Option value="audit-ai">Audit Ai</Option>
                                      <Option value="gpt-5">GPT-5</Option>
                                      <Option value="grok">Grok</Option>
                                      <Option value="gemini">Gemini</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Form.Item
                                label="Özel Prompt"
                                name="prompt"
                              >
                                <TextArea 
                                  rows={2}
                                  placeholder="Audit analizi için özel talimatlar... (İsteğe bağlı)"
                                />
                              </Form.Item>
                            </>
                          )}

                          {/* Animasyon Göstergesi */}
                          {auditAnimation && (
                            <div style={{
                              marginTop: 16,
                              padding: '20px',
                              backgroundColor: '#f6f8fa',
                              borderRadius: '8px',
                              border: '2px solid #1890ff',
                              textAlign: 'center'
                            }}>
                              <div style={{ marginBottom: 16 }}>
                                <div style={{
                                  display: 'inline-block',
                                  width: '40px',
                                  height: '40px',
                                  border: '3px solid #1890ff',
                                  borderTop: '3px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }} />
                              </div>
                              
                              {/* Progress Bar */}
                              <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#e6f7ff',
                                borderRadius: '4px',
                                marginBottom: '16px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  height: '100%',
                                  backgroundColor: '#1890ff',
                                  borderRadius: '4px',
                                  width: `${progressPercentage}%`,
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              
                              {/* Step Text */}
                              <div style={{
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#1890ff',
                                marginBottom: '8px'
                              }}>
                                {getStepText()}
                              </div>
                              
                              {/* Step Details */}
                              <div style={{
                                fontSize: '12px',
                                color: '#666',
                                marginBottom: '16px'
                              }}>
                                Adım {currentStep + 1} / 5
                              </div>
                              
                              {/* Step Indicators */}
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                {[0, 1, 2, 3, 4].map(step => (
                                  <div key={step} style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: step <= currentStep ? '#1890ff' : '#e6f7ff',
                                    border: '1px solid #1890ff',
                                    transition: 'all 0.3s ease'
                                  }} />
                                ))}
                              </div>
                            </div>
                          )}

                          <Button
                            type="primary"
                            size="large"
                            loading={auditButtonLoading && !auditAnimation}
                            disabled={auditAnimation}
                            onClick={handleAuditStart}
                            style={{ width: '100%', marginTop: 16 }}
                          >
                            {auditAnimation ? '🔄 Audit Devam Ediyor...' : '✨ Audit Başlat'}
                          </Button>
                        </>
                      )}
                    </Form>
                  </div>
                )
              },
              {
                key: 'history',
                label: 'Audit Geçmişi',
                children: audits.length === 0 ? (
            <div style={{ textAlign: 'center', margin: '40px 0' }}>
              <div style={{ marginBottom: 24 }}>
                <Title level={4} style={{ color: '#666', marginBottom: 8 }}>
                  Henüz audit yapılmamış
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Bu marka için Audit Ai analizi mevcut değil
                </Text>
              </div>
              
              <button 
                className="design-btn rotating-gradient text-sm"
                onClick={() => {
                  message.info('Audit Ai otomatik olarak markanızın Google Ads ve Meta Ads verilerini analiz ederek performans önerileri sunmaktadır. Analiz tamamlandığında bu sayfada sonuçları görüntüleyebilirsiniz.', 6);
                }}
                style={{ marginBottom: 16 }}
              >
                <span style={{ marginRight: 8 }}>✨</span>
                Audit Ai
              </button>
              
              <div style={{ 
                background: '#f6f8fa', 
                padding: 12, 
                borderRadius: 6,
                fontSize: 12,
                color: '#666'
              }}>
                Ai teknolojisi ile performans analizi ve öneriler
              </div>
            </div>
          ) : (
            <List
              itemLayout="vertical"
              dataSource={audits}
              renderItem={(audit) => (
                <List.Item
                  key={audit.id}
                  style={{
                    borderLeft: `4px solid #52c41a`,
                    paddingLeft: 8,
                    marginBottom: 8,
                    backgroundColor: '#fafafa',
                    borderRadius: 6,
                    padding: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleViewDetails(audit)}
                  actions={[
                    <Button 
                      type="primary" 
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(audit);
                      }}
                      style={{ fontSize: '12px' }}
                    >
                      <span className="hidden sm:inline">Detayları Gör</span>
                      <span className="sm:hidden">Detay</span>
                    </Button>
                  ]}
                >
                  {/* Mobile Layout */}
                  <div className="block sm:hidden">
                    <Row gutter={8} align="middle">
                      <Col span={4}>
                        {getAuditIcon(audit)}
                      </Col>
                      <Col span={16}>
                        <div>
                          <Text strong style={{ fontSize: 14 }}>
                            {getAuditTitle(audit)}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            📊 {getDateRangeText(audit)}
                          </Text>
                        </div>
                      </Col>
                      <Col span={4} style={{ textAlign: 'right' }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          {dayjs(audit.created_at).format('DD.MM')}
                        </Text>
                      </Col>
                    </Row>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:block">
                    <Row gutter={16} align="middle">
                      <Col span={3}>
                        {getAuditIcon(audit)}
                      </Col>
                      <Col span={10}>
                        <Text strong style={{ fontSize: 15 }}>
                          {getAuditTitle(audit)}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          📊 {getDateRangeText(audit)}
                        </Text>
                      </Col>
                      <Col span={6} style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(audit.created_at).format('DD MMM YYYY')}
                        </Text>
                      </Col>
                      <Col span={5} style={{ textAlign: 'right' }}>
                        {audit.response_time && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            ⚡ {audit.response_time}
                          </Text>
                        )}
                      </Col>
                    </Row>
                  </div>
                </List.Item>
              )}
            />
          )
              }
            ]}
          />
        </Spin>
      </Modal>

      {/* Audit Detail Modal */}
      <Modal
        title={
          selectedAudit ? (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                {selectedAudit.auditType === 'google_ads' ? 'Google Ads Audit Ai Analiz Sonucu' :
                 selectedAudit.auditType === 'facebook' ? 'Meta Ads Audit Ai Analiz Sonucu' :
                 selectedAudit.auditType === 'google_analytics' ? 'Google Analytics 4 Audit Ai Analiz Sonucu' :
                 selectedAudit.auditType === 'custom_data' ? 'ReportsUp Audit Ai Analiz Sonucu' :
                 'Ai Analiz Sonucu'}
              </div>
              <div style={{ fontSize: 12, color: '#666', fontWeight: 'normal' }}>
                {dayjs(selectedAudit.created_at).format('DD MMMM YYYY, HH:mm')} • {getDateRangeLabel(selectedAudit.dateRange)}
              </div>
            </div>
          ) : 'Ai Analiz Sonucu'
        }
        open={detailModalVisible}
        onCancel={handleDetailClose}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<FilePdfOutlined />}
              onClick={downloadAuditPDF}
            >
              PDF İndir
            </Button>
          </div>
        }
        width={window.innerWidth < 640 ? '95vw' : 1000}
        style={{ top: window.innerWidth < 640 ? 20 : 30 }}
        bodyStyle={{ 
          maxHeight: window.innerWidth < 640 ? '75vh' : '70vh', 
          overflow: 'auto',
          padding: window.innerWidth < 640 ? '12px' : '20px'
        }}
        zIndex={1100}
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
      >
        {selectedAudit && (
          <div>
            {/* Analiz Tarihi Bilgisi */}
            <div style={{ 
              marginBottom: 16, 
              padding: 12, 
              backgroundColor: '#f0f9ff', 
              borderRadius: 6,
              border: '1px solid #e0f2fe'
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                📊 Analiz Edilen Dönem: <Text strong>{getDateRangeText(selectedAudit)}</Text>
              </Text>
            </div>

            {/* AI Analiz Sonucu */}
            {selectedAudit.status === 'success' && selectedAudit.analysis && (
              <div>
                <div
                  style={{ 
                    fontSize: 14,
                    lineHeight: '1.6',
                    backgroundColor: '#f9f9f9',
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #e8e8e8',
                    margin: 0,
                    position: 'relative'
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => navigator.clipboard.writeText(selectedAudit.analysis)}
                    style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                  >
                    Kopyala
                  </Button>
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: renderMarkdown(selectedAudit.analysis) 
                    }}
                    style={{ marginRight: 60 }}
                  />
                </div>
              </div>
            )}

            {selectedAudit.status === 'error' && selectedAudit.error_message && (
              <Alert
                message="Audit Hatası"
                description={selectedAudit.error_message}
                type="error"
                showIcon
              />
            )}

            {selectedAudit.status === 'pending' && (
              <Alert
                message="Audit Devam Ediyor"
                description="Bu audit henüz tamamlanmadı. Lütfen bekleyin."
                type="info"
                showIcon
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default BrandAuditHistoryModal;